import { type Registry as Services, service } from '@ember/service';
import Model, { belongsTo, type HasMany, hasMany } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import dayjs from 'dayjs';
import type NetRevenueCostProfile from 'lumen/models/net-revenue/cost-profile';
import type NetRevenueStayDatePoint from 'lumen/models/net-revenue/stay-date-point';
import type NetRevenueTotals from 'lumen/models/net-revenue/totals';
import {
  DATE_FORMAT,
  STAY_DATE_GRANULARITIES,
  type StayDateGranularity,
} from 'lumen/utils/constants/net-revenue-constants';
import { NORMALIZED_PERIOD } from 'lumen/utils/constants/ri-constant-values';
import {
  calculateAdr,
  calculateAverageCostPerRoomNight,
  calculateCostToRoomRevenueRatio,
  calculateNetAdr,
  calculateNetRevenueToRoomRevenueRatio,
  calculateOccupancyRate,
  calculateRelativeChange,
} from 'lumen/utils/net-revenue/metrics';
import type {
  CostProfileData,
  CostTypeComparison,
  CostTypeRow,
  Kpi,
  KpiData,
  NetRevenueReference,
  StayDateComparisonPoint,
  StayDateData,
  StayDatePoint,
} from 'lumen/utils/net-revenue/types';

interface StayDateBucket {
  capacity: number;
  comparisonCapacity: number;
  comparisonCost: number;
  comparisonNetRevenue: number;
  comparisonRoomNights: number;
  comparisonRoomRevenue: number;
  cost: number;
  hasComparison: boolean;
  label: string;
  netRevenue: number;
  roomNights: number;
  roomRevenue: number;
}

function buildKpi(
  value: number,
  comparison: number | undefined,
  overrides: Partial<Kpi> = {},
): Kpi {
  return {
    changePercent: calculateRelativeChange(value, comparison),
    invertColors: false,
    value,
    ...overrides,
  };
}

function toStayComparison(
  reference: NetRevenueReference,
  fallbackCapacity: number,
): StayDateComparisonPoint {
  return {
    cost: reference.cost ?? 0,
    netRevenue: reference.netRevenue,
    occupancy:
      calculateOccupancyRate(
        reference.roomNights,
        reference.capacity ?? fallbackCapacity,
      ) ?? 0,
    roomRevenue: reference.roomRevenue,
  };
}

function toCostComparison(reference: NetRevenueReference): CostTypeComparison {
  const cost = reference.cost ?? 0;
  return {
    adr: calculateAdr(reference.roomRevenue, reference.roomNights) ?? 0,
    cost,
    costPercent:
      calculateCostToRoomRevenueRatio(cost, reference.roomRevenue) ?? 0,
    netAdr: calculateNetAdr(reference.netRevenue, reference.roomNights) ?? 0,
    netRevenue: reference.netRevenue,
    netRevenuePercent:
      calculateNetRevenueToRoomRevenueRatio(
        reference.netRevenue,
        reference.roomRevenue,
      ) ?? 0,
    roomNights: reference.roomNights,
    roomRevenue: reference.roomRevenue,
  };
}

export default class NetRevenueDashboard extends Model {
  [Type] = 'net-revenue/dashboard' as const;

  @belongsTo('net-revenue/totals', { async: false, inverse: null })
  declare total: NetRevenueTotals;
  @hasMany('net-revenue/cost-profile', { async: false, inverse: null })
  declare costProfiles: HasMany<NetRevenueCostProfile>;
  @hasMany('net-revenue/stay-date-point', { async: false, inverse: null })
  declare stayDate: HasMany<NetRevenueStayDatePoint>;
  @service declare intl: Services['intl'];

  #bucketStayDate(
    granularity: 'week' | 'month',
    referenceType: string,
  ): StayDatePoint[] {
    const periodKey = NORMALIZED_PERIOD[granularity];
    const bucketsByStart = new Map<string, StayDateBucket>();

    this.stayDate.forEach((point) => {
      const label = dayjs
        .utc(point.date)
        .startOf(periodKey)
        .format(DATE_FORMAT);
      const reference = point.referenceByType(referenceType);
      const bucket = bucketsByStart.get(label) ?? {
        capacity: 0,
        comparisonCapacity: 0,
        comparisonCost: 0,
        comparisonNetRevenue: 0,
        comparisonRoomNights: 0,
        comparisonRoomRevenue: 0,
        cost: 0,
        hasComparison: false,
        label,
        netRevenue: 0,
        roomNights: 0,
        roomRevenue: 0,
      };

      bucket.capacity += point.capacity;
      bucket.cost += point.cost ?? 0;
      bucket.netRevenue += point.netRevenue;
      bucket.roomNights += point.roomNights;
      bucket.roomRevenue += point.roomRevenue;

      if (reference) {
        bucket.hasComparison = true;
        bucket.comparisonCapacity += reference.capacity ?? point.capacity;
        bucket.comparisonCost += reference.cost ?? 0;
        bucket.comparisonNetRevenue += reference.netRevenue;
        bucket.comparisonRoomNights += reference.roomNights;
        bucket.comparisonRoomRevenue += reference.roomRevenue;
      }

      bucketsByStart.set(label, bucket);
    });

    return Array.from(bucketsByStart.values()).map((bucket) => ({
      comparison: bucket.hasComparison
        ? {
            cost: bucket.comparisonCost,
            netRevenue: bucket.comparisonNetRevenue,
            occupancy:
              calculateOccupancyRate(
                bucket.comparisonRoomNights,
                bucket.comparisonCapacity,
              ) ?? 0,
            roomRevenue: bucket.comparisonRoomRevenue,
          }
        : null,
      cost: bucket.cost,
      label: bucket.label,
      netRevenue: bucket.netRevenue,
      occupancy:
        calculateOccupancyRate(bucket.roomNights, bucket.capacity) ?? 0,
      roomRevenue: bucket.roomRevenue,
    }));
  }

  buildCostProfile(
    referenceType: string,
    comparisonLabel: string | null,
  ): CostProfileData {
    const total = this.total;
    const remaining = this.costProfiles.find((profile) => profile.isRemaining);

    const rows: CostTypeRow[] = this.costProfiles
      .filter((profile) => !profile.isRemaining)
      .map((profile) => {
        const reference = profile.referenceByType(referenceType);
        return {
          adr: profile.adr,
          comparison: reference ? toCostComparison(reference) : null,
          cost: profile.cost ?? 0,
          costPercent: profile.costPercent,
          name: profile.name!,
          netAdr: profile.netAdr,
          netRevenue: profile.netRevenue,
          netRevenuePercent: profile.netRevenuePercent,
          roomNights: profile.roomNights,
          roomRevenue: profile.roomRevenue,
        };
      });

    return {
      comparisonLabel,
      remainingReservations: remaining
        ? {
            roomNights: remaining.roomNights,
            roomRevenue: remaining.roomRevenue,
          }
        : null,
      rows,
      totals: {
        comparison: null,
        cost: total.cost ?? 0,
        costPercent: total.costPercent,
        netAdr: total.netAdr,
        netRevenue: total.netRevenue,
        netRevenuePercent: total.netRevenuePercent,
        roomNights: total.roomNights,
        roomRevenue: total.roomRevenue,
      },
    };
  }

  buildKpiData(referenceType: string): KpiData {
    const total = this.total;
    const reference = total.referenceByType(referenceType);

    return {
      avgCost: buildKpi(
        total.avgCost,
        reference
          ? (calculateAverageCostPerRoomNight(
              reference.cost ?? 0,
              reference.roomNights,
            ) ?? 0)
          : undefined,
        {
          invertColors: true,
          label: this.intl.t('net_revenue.per_room_night'),
        },
      ),
      cost: buildKpi(
        total.cost ?? 0,
        reference ? (reference.cost ?? 0) : undefined,
        { invertColors: true, percentOfRevenue: total.costPercent },
      ),
      netAdr: buildKpi(
        total.netAdr,
        reference
          ? (calculateNetAdr(reference.netRevenue, reference.roomNights) ?? 0)
          : undefined,
      ),
      netRevenue: buildKpi(total.netRevenue, reference?.netRevenue, {
        percentOfRevenue: total.netRevenuePercent,
      }),
    };
  }

  buildStayData(
    granularity: StayDateGranularity,
    referenceType: string,
    comparisonLabel: string | null,
  ): StayDateData {
    if (granularity === STAY_DATE_GRANULARITIES.DAY) {
      const points: StayDatePoint[] = this.stayDate.map((point) => {
        const reference = point.referenceByType(referenceType);
        return {
          comparison: reference
            ? toStayComparison(reference, point.capacity)
            : null,
          cost: point.cost ?? 0,
          label: point.date,
          netRevenue: point.netRevenue,
          occupancy: point.occupancy,
          roomRevenue: point.roomRevenue,
        };
      });
      return { comparisonLabel, points };
    }

    return {
      comparisonLabel,
      points: this.#bucketStayDate(granularity, referenceType),
    };
  }
}
